import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetVoucherBatchesQuery, useDeleteVoucherBatchMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import DateRangePicker from '../../../components/DateRangePicker';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { showSuccess, showError } from '../../../utils/toast';
import config from '../../../config/env';

interface AdminVoucherBatchesProps {
  type: 'card' | 'voucher';
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const AdminVoucherBatches: React.FC<AdminVoucherBatchesProps> = ({ type }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [daterange, setDaterange] = useState('');
  const [page, setPage] = useState(1);
  const defaultCurrency = config.currencySymbol || '$';
  
  const { data, isLoading, error, refetch } = useGetVoucherBatchesQuery({ 
    search, 
    daterange, 
    page, 
    batch_type: type 
  });
  
  const [deleteVoucherBatch] = useDeleteVoucherBatchMutation();

  useEffect(() => {
    setPage(1);
  }, [search, daterange, type]);

  const handleDelete = async (id: number) => {
    try {
      const result = await deleteVoucherBatch(id).unwrap();
      showSuccess(result.message || 'Batch deleted successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to delete batch. Please try again.');
    }
  };

  const title = type === 'card' ? 'Fesilah Cards' : 'Fesilah Vouchers';
  const addPath = type === 'card' ? '/admin/fesilah-cards/add' : '/admin/fesilah-vouchers/add';
  const editPathBase = type === 'card' ? '/admin/fesilah-cards' : '/admin/fesilah-vouchers';

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">Error loading batches</div>;
  }

  const batches = data?.data || { data: [], current_page: 1, last_page: 1 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{title}</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary">Home</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">{title}</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <form onSubmit={(e) => { e.preventDefault(); }}>
                    <div className="d-flex align-items-center position-relative my-1">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-350px ps-15 me-5"
                        placeholder="Search Batch Name/ID..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <div className="position-relative d-flex align-items-center">
                        <DateRangePicker
                          value={daterange}
                          onChange={setDaterange}
                          placeholder="Date Range"
                        />
                        {(search || daterange) && (
                          <button
                            type="button"
                            onClick={() => { setSearch(''); setDaterange(''); }}
                            className="btn btn-sm btn-light-primary me-3"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
                <div className="card-toolbar">
                  <button
                    type="button"
                    className="btn btn-base"
                    onClick={() => navigate(addPath)}
                  >
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor"></rect>
                        <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="currentColor"></rect>
                      </svg>
                    </span>
                    Add New Batch
                  </button>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>#</th>
                        <th>Batch ID</th>
                        <th>Name</th>
                        <th>Value ({defaultCurrency})</th>
                        <th>Quantity</th>
                        <th>Created Date</th>
                        <th>Expired Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {batches.data.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-10">
                            <span className="text-muted">No batches found</span>
                          </td>
                        </tr>
                      ) : (
                        batches.data.map((batch: any, index: number) => (
                          <tr key={batch.id}>
                            <td>{(batches.current_page - 1) * 15 + index + 1}</td>
                            <td>{batch.batch_identifier}</td>
                            <td>{batch.name}</td>
                            <td>{batch.card_value} {batch.currency || defaultCurrency}</td>
                            <td>{batch.quantity}</td>
                            <td>
                              <i className="fas fa-calendar-alt me-1"></i>
                              {formatDate(batch.created_at)}
                            </td>
                            <td>
                              <i className="fas fa-calendar-alt me-1"></i>
                              {formatDate(batch.expired_at)}
                            </td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon btn-light-info w-125px"
                                  onClick={() => navigate(`${editPathBase}/${batch.id}/vouchers`)}
                                  title="View Vouchers"
                                >
                                  <i className="fas fa-list"></i>
                                  View Vouchers
                                </button>
                                <ActionButtons
                                  onEdit={() => navigate(`${editPathBase}/${batch.id}/edit`)}
                                  onDelete={() => handleDelete(batch.id)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {batches.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={batches.current_page}
                      lastPage={batches.last_page}
                      total={batches.total || 0}
                      perPage={batches.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminVoucherBatches;
